import api from '@/services/api/client';
import { mediaUrlForPlayback } from '@/lib/mediaUrls';

/** Normalized crop boxes from narration image-story API (per-mille of width/height). */

export function cropBoxesEqual(a, b) {
  if (!a || !b) return false;
  return ['x', 'y', 'width', 'height'].every(
    (k) => Math.abs(Number(a[k]) - Number(b[k])) < 0.05,
  );
}

/** Match narration scene rows by stable client_id or segment number fallback. */
export function findImageStoryScene(scenes, { clientId, segmentNumber } = {}) {
  if (!Array.isArray(scenes) || !scenes.length) return null;
  const cid = (clientId || '').trim();
  if (cid) {
    const byId = scenes.find((s) => s?.client_id === cid);
    if (byId) return byId;
    const snFromClient = cid.match(/^segment-(\d+)$/);
    if (snFromClient) {
      const bySn = scenes.find(
        (s) => Number(s?.segment_number) === Number(snFromClient[1]),
      );
      if (bySn) return bySn;
    }
  }
  if (segmentNumber != null && String(segmentNumber).trim() !== '') {
    return (
      scenes.find((s) => Number(s?.segment_number) === Number(segmentNumber)) || null
    );
  }
  return null;
}

export const FULL_FRAME_CROP_BOX = { x: 0, y: 0, width: 1000, height: 1000 };

/** Stable cache-buster for scene thumbnails after crop_box changes. */
export function cropBoxCacheKey(cropBox) {
  if (!cropBox || typeof cropBox !== 'object') return '';
  const x = Number(cropBox.x) || 0;
  const y = Number(cropBox.y) || 0;
  const w = Number(cropBox.width) || 0;
  const h = Number(cropBox.height) || 0;
  return `${x}-${y}-${w}-${h}`;
}

/** Playback URL with cache-bust when crop metadata changes. */
export function sceneImageUrlWithCropRevision(imageUrl, scene) {
  const base = mediaUrlForPlayback(imageUrl);
  if (!base || base.startsWith('blob:') || base.startsWith('data:')) return base;
  const rev = scene?.crop_revision || cropBoxCacheKey(scene?.crop_box);
  if (!rev) return base;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}crop=${encodeURIComponent(String(rev))}`;
}

function parseMediaAssetId(url) {
  const match = String(url || '').match(/\/api\/media\/([^/?#]+)/);
  return match ? match[1] : null;
}

async function blobFromAuthenticatedMediaUrl(playbackUrl, originalUrl) {
  const assetId = parseMediaAssetId(playbackUrl);
  if (assetId) {
    try {
      const { data } = await api.get(`/media/${encodeURIComponent(assetId)}`, {
        responseType: 'blob',
        maxRedirects: 5,
      });
      if (data && data.size > 0) return data;
    } catch {
      /* fall through to narration proxy */
    }
  }

  const { data } = await api.post(
    '/narration/image-story/fetch-sheet-image',
    { image_url: originalUrl },
    { responseType: 'blob' },
  );
  if (!data || data.size <= 0) {
    throw new Error('Could not load storyboard image for cropping');
  }
  return data;
}

/**
 * Map normalized crop coords onto the painted image area inside object-contain.
 * Returns layout relative to container scroll origin (ox, oy, dispW, dispH, nw, nh).
 */
export function computeObjectContainImageLayout(img, container) {
  if (!img || !container) return null;
  const nw = img.naturalWidth;
  const nh = img.naturalHeight;
  if (!nw || !nh) return null;

  const cr = container.getBoundingClientRect();
  const ir = img.getBoundingClientRect();
  const boxW = ir.width;
  const boxH = ir.height;
  if (boxW < 1 || boxH < 1) return null;

  const mediaAr = nw / nh;
  const boxAr = boxW / boxH;
  let dispW;
  let dispH;
  if (mediaAr > boxAr) {
    dispW = boxW;
    dispH = boxW / mediaAr;
  } else {
    dispH = boxH;
    dispW = boxH * mediaAr;
  }

  const padX = (boxW - dispW) / 2;
  const padY = (boxH - dispH) / 2;

  return {
    ox: ir.left - cr.left + container.scrollLeft + padX,
    oy: ir.top - cr.top + container.scrollTop + padY,
    dispW,
    dispH,
    nw,
    nh,
  };
}

/**
 * Remote / S3 images taint the canvas — load via authenticated API into a blob: URL.
 * Caller should revoke the returned URL when done if it differs from imageSrc.
 */
export async function resolveImageSrcForCanvasCrop(imageSrc) {
  const raw = String(imageSrc || '').trim();
  if (!raw || raw.startsWith('blob:') || raw.startsWith('data:')) return raw;
  const playbackUrl = mediaUrlForPlayback(raw);
  if (!playbackUrl) {
    throw new Error('Could not load storyboard image for cropping');
  }
  const blob = await blobFromAuthenticatedMediaUrl(playbackUrl, raw);
  return URL.createObjectURL(blob);
}

export function normalizedToDisplayRect(box, layout) {
  if (!box || !layout) return null;
  const x = Number(box.x);
  const y = Number(box.y);
  const w = Number(box.width);
  const h = Number(box.height);
  if ([x, y, w, h].some((n) => Number.isNaN(n))) return null;
  const { ox, oy, dispW, dispH } = layout;
  return {
    left: ox + (x / 1000) * dispW,
    top: oy + (y / 1000) * dispH,
    width: (w / 1000) * dispW,
    height: (h / 1000) * dispH,
  };
}

export function displayRectToNormalized(rect, layout) {
  const { ox, oy, dispW, dispH } = layout;
  let left = rect.left - ox;
  let top = rect.top - oy;
  let w = rect.width;
  let h = rect.height;

  left = Math.max(0, Math.min(dispW - 2, left));
  top = Math.max(0, Math.min(dispH - 2, top));
  w = Math.max(2, Math.min(dispW - left, w));
  h = Math.max(2, Math.min(dispH - top, h));

  const norm = {
    x: Math.round((left / dispW) * 1000 * 100) / 100,
    y: Math.round((top / dispH) * 1000 * 100) / 100,
    width: Math.round((w / dispW) * 1000 * 100) / 100,
    height: Math.round((h / dispH) * 1000 * 100) / 100,
  };

  norm.x = Math.min(norm.x, 1000 - norm.width);
  norm.y = Math.min(norm.y, 1000 - norm.height);

  return norm;
}

/**
 * Raster crop using normalized box vs natural image dimensions.
 * @returns {Promise<Blob | null>}
 */
export async function blobFromNormalizedCrop(imageSrc, cropNorm, mimeType = 'image/png') {
  const raw = String(imageSrc || '').trim();
  if (!raw) return null;

  let src = raw;
  let tempObjectUrl = null;
  try {
    if (!src.startsWith('blob:') && !src.startsWith('data:')) {
      tempObjectUrl = await resolveImageSrcForCanvasCrop(src);
      src = tempObjectUrl;
    }

    const img = new Image();
    const loaded = new Promise((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Image failed to load'));
    });
    img.src = src;
    await loaded;

    const nw = img.naturalWidth || img.width;
    const nh = img.naturalHeight || img.height;
    if (!nw || !nh) return null;

    const x = (Number(cropNorm.x) / 1000) * nw;
    const y = (Number(cropNorm.y) / 1000) * nh;
    const w = (Number(cropNorm.width) / 1000) * nw;
    const h = (Number(cropNorm.height) / 1000) * nh;

    const sx = Math.max(0, Math.round(x));
    const sy = Math.max(0, Math.round(y));
    const sw = Math.max(1, Math.min(nw - sx, Math.round(w)));
    const sh = Math.max(1, Math.min(nh - sy, Math.round(h)));

    const canvas = document.createElement('canvas');
    canvas.width = sw;
    canvas.height = sh;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    try {
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
    } catch {
      return null;
    }

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => resolve(blob || null),
        mimeType,
        0.92,
      );
    });
  } finally {
    if (tempObjectUrl) URL.revokeObjectURL(tempObjectUrl);
  }
}

/** Returns blob: object URL for previews; prefer blobFromNormalizedCrop + uploadSceneImageFromBlob for uploads. */
export async function blobUrlFromNormalizedCrop(imageSrc, cropNorm, mimeType = 'image/png') {
  const blob = await blobFromNormalizedCrop(imageSrc, cropNorm, mimeType);
  if (!blob) return null;
  return URL.createObjectURL(blob);
}
